import { getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import {
  Event,
  EventNames,
  FLAG_REPLY,
  FriendList,
  FriendSentList,
  FriendData,
  FriendSentListData,
  FriendApprovedData,
  FriendRejectedData,
  FriendStatus,
  FriendDeleteData,
  FriendCommonData,
  MessageTypes,
  MessageStatus,
  ContactStatus,
} from "~proto"
import { FriendModel } from "../friend/friend"
// import { compareDayAgo } from "~utils"
import { RootStoreModel } from "~models/root-store"
import { NotifyType } from "~types/message"

/**
 * Model description here for TypeScript hints.
 */
export const FriendStoreModel = types
  .model("FriendStore")
  .props({ 
    friends: types.optional(types.map(FriendModel), {}),
    friendSentList: types.optional(types.map(FriendModel), {}),
  })
  .volatile(() => ({
    skipRemoteRefreshUntil: 0,
  }))
  .views((self) => ({
    friendsByUserId(id: number) {
      return Array.from(self.friends.values()).find((x) => x.user.id === id)
    },
    get friendsCount() {
      return Array.from(self.friends.values()).filter(
        (i) => !i.readed && i.status === FriendStatus.PENDING,
      ).length
    },
    get friendsByStatus() {
      const { contactStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const list = [
        { title: "待处理", data: [] },
        { title: "已处理", data: [] },
      ]
      Array.from(self.friends.values())
        .sort((a, b) => (a.created > b.created ? -1 : 1))
        .forEach((i) => {
          const contact = contactStore.byUserId(i.user_id)
          
          if (
            (i.status !== FriendStatus.PENDING && i.status !== FriendStatus.APPROVED) ||
            (i.status === FriendStatus.APPROVED && !contact)
          )
            return
          if (i.status === 1||i.status === 2||i.status === 3) {
            list[1].data.push(i)
          } else {
            list[0].data.push(i)
          }
        })
      return list
    },
    friendSentListByUserId(id: number) {
      return Array.from(self.friendSentList.values()).find((x) => x.user.id === id)
    },
    get friendSentListCount() {
      return Array.from(self.friendSentList.values()).filter(
        (i) => !i.readed && i.status === FriendStatus.PENDING,
      ).length
    },
    get friendSentListByStatus() {
      const list = [
        { title: "等待回复", data: [] },
        { title: "已回复", data: [] },
      ]
      Array.from(self.friendSentList.values())
        .sort((a, b) => (a.created > b.created ? -1 : 1))
        .forEach((i) => {
          list[i.status === FriendStatus.PENDING ? 0 : 1].data.push(i)
        })
      return list
    },
  }))
  .actions((self) => {
    function friendSentListPut(item: FriendSentListData, userRef: number) {
      const targetUserId = item.user?.id
      if (targetUserId != null) {
        const duplicate = Array.from(self.friendSentList.values()).find(
          (x) => x.user?.id === targetUserId && x.id !== item.id,
        )
        if (duplicate) {
          self.friendSentList.delete(duplicate.id.toString())
        }
      }
      self.friendSentList.put({ ...item, user: userRef, readed: false })
    }

    function friendCreateHandle({ user, ...rest }: FriendData) {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const u = userStore.addUser(user)
      if (userStore.currentUser.id === rest.user_id) {
        friendSentListPut({ ...rest, user }, u.id)
        return self.friendSentList.get(rest.id.toString())
      }
      return self.friends.put({ ...rest, user: u.id, readed: false })
    }

    function normalizeFriendData(reply: unknown): FriendData | null {
      if (!reply || typeof reply !== "object") return null
      const data = reply as Record<string, unknown>
      if (data.id != null && data.user_id != null && data.user) {
        return reply as FriendData
      }
      const items = data.items
      if (Array.isArray(items) && items[0]) {
        return items[0] as FriendData
      }
      return null
    }

    function onFriendAddReply(reply: unknown, request?: FriendCommonData) {
      const data = normalizeFriendData(reply)
      if (data) {
        friendCreateHandle(data)
        return
      }
      const userId = request?.user_id
      if (!userId) return
      const existing = self.friendSentListByUserId(userId)
      if (existing) {
        existing.setStatus(FriendStatus.PENDING)
      }
    }

    function markSkipRefreshUntil(ms = 1500) {
      self.skipRemoteRefreshUntil = Date.now() + ms
    }

    function refreshFriendLists() {
      if (Date.now() < self.skipRemoteRefreshUntil) return
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      eventStore.publish(new Event(EventNames.FRIEND_LIST, {}), {
        flags: FLAG_REPLY,
        onData: (data: FriendList) => {
          if (!data?.items) return
          self.friends.clear()
          data.items.forEach((item) => friendCreateHandle(item))
        },
      })
      eventStore.publish(new Event(EventNames.FRIEND_SENT_LIST, {}), {
        flags: FLAG_REPLY,
        onData: (data: FriendSentList) => {
          if (!data?.items) return
          self.friendSentList.clear()
          friendSentListHandle(data.items)
        },
      })
    }
    // event-store
    function onFriendCreated({ data }: Event<FriendData>) {
      return friendCreateHandle(data)
    }
    // event-store
    function onFriendList({ data: { items } }: Event<FriendList>) {
      items.forEach((data) => friendCreateHandle(data))
    }
    function onFriendSentList({ data }: Event<FriendSentList>) {
      return friendSentListHandle(data.items || [])
    }
    function friendSentListHandle(items: FriendSentListData[]) {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      return items.forEach((item) => {
        const u = userStore.addUser(item.user)
        friendSentListPut(item, u.id)
      })
    }
    function onRead(id: number) {
      const friend = self.friends.get(id.toString())
      friend.setRead()
    }
    // event-store
    function onFriendApproved({ data }: Event<FriendApprovedData>) {
      const friend = self.friendsByUserId(data.id)
      friend?.setStatus(FriendStatus.APPROVED)
      const friendSent = self.friendSentListByUserId(data.id)
      friendSent?.setStatus(FriendStatus.APPROVED)
      const { contactStore, chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const contact = contactStore.byUserId(data.id)
      contact?.setStatus(ContactStatus.CONFIRM)

      let chat = chatStore.getPrivateChatByUserID(data.id)
      if (!chat) {
        chat = chatStore.newPrivateChat({
          receiver_id: data.id,
          name: data.username,
        })
      }
      chat.setDeleted(false)
      chatStore.reviveChat(chat)

      const hasGreeting = chat.messages.some(
        (m) => m.notify_type === NotifyType.CONTACT_CREATED,
      )
      if (!hasGreeting) {
        chat.newMessage({
          content: "现在我们是朋友了，可以开始聊天了",
          type: MessageTypes.TEXT,
          status: MessageStatus.READ,
          notify_type: NotifyType.CONTACT_CREATED,
        })
      }
    }
    // event-store
    function onFriendRejected({ data }: Event<FriendRejectedData>) {
      // 被拒绝方：更新“我发起的好友申请”
      const sent = self.friendSentListByUserId(data.id)
      sent?.setStatus(FriendStatus.REJECTED)

      // 拒绝方：更新“新朋友(收到的申请)”
      const received = self.friendsByUserId(data.id)
      received?.setStatus(FriendStatus.REJECTED)
      const friendSent = self.friendSentListByUserId(data.id)
      friendSent?.setStatus(FriendStatus.REJECTED)
    }
    // event-store
    function onFriendDeleted({ data: { id } }: Event<FriendDeleteData>) {
      self.friends.delete(id.toString())
      self.friendSentList.delete(id.toString())
    }
    return {
      onFriendCreated,
      onFriendList,
      onFriendSentList,
      onFriendAddReply,
      refreshFriendLists,
      markSkipRefreshUntil,
      onRead,
      onFriendApproved,
      onFriendRejected,
      onFriendDeleted,
    }
  }) // eslint-disable-line @typescript-eslint/no-unused-vars

type FriendStoreType = Instance<typeof FriendStoreModel>
export interface FriendStore extends FriendStoreType {}
type FriendStoreSnapshotType = SnapshotOut<typeof FriendStoreModel>
export interface FriendStoreSnapshot extends FriendStoreSnapshotType {}
export const createFriendStoreDefaultModel = () => types.optional(FriendStoreModel, {})
